/*
 * Helpers
 */
const clamp = (v: number, max: number) => Math.min(Math.max(v, 0), max)

const clamp01 = (v: number) => clamp(v, 1)

const CSS_INTEGER = '[-\\+]?\\d+%?'
const CSS_NUMBER = '[-\\+]?\\d*\\.\\d+%?'
const CSS_UNIT = `(?:${CSS_NUMBER})|(?:${CSS_INTEGER})`

const PERMISSIVE_MATCH3 = `[\\s|\\(]+(${CSS_UNIT})[,|\\s]+(${CSS_UNIT})[,|\\s]+(${CSS_UNIT})\\s*\\)?`
const PERMISSIVE_MATCH4 = `[\\s|\\(]+(${CSS_UNIT})[,|\\s]+(${CSS_UNIT})[,|\\s]+(${CSS_UNIT})[,|\\s]+(${CSS_UNIT})\\s*\\)?`

const MATCHERS = {
  rgb: new RegExp(`rgb${PERMISSIVE_MATCH3}`),
  rgba: new RegExp(`rgba${PERMISSIVE_MATCH4}`),
  hsl: new RegExp(`hsl${PERMISSIVE_MATCH3}`),
  hsla: new RegExp(`hsla${PERMISSIVE_MATCH4}`),
  hsv: new RegExp(`hsv${PERMISSIVE_MATCH3}`),
  hsva: new RegExp(`hsva${PERMISSIVE_MATCH4}`),
  hex3: /^#?([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})$/,
  hex6: /^#?([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/,
  hex4: /^#?([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})$/,
  hex8: /^#?([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/,
}

export interface RGB {
  r: number
  g: number
  b: number
}

export interface RGBA extends RGB {
  a: number
}

export interface HSL {
  h: number
  s: number
  l: number
  a: number
}

export class Color {
  readonly _r: number = 0
  readonly _g: number = 0
  readonly _b: number = 0
  readonly _a: number = 1
  readonly _isValid: boolean = false

  constructor(color: RGBA | string, alpha?: number) {
    if (typeof color === 'string') {
      color = Color.parseString(color)
    }

    if (color) {
      this._r = clamp(color.r, 255)
      this._g = clamp(color.g, 255)
      this._b = clamp(color.b, 255)
      this._a = clamp01(typeof alpha !== 'undefined' ? alpha : color.a)
      this._isValid = true
    }
  }

  public static fromString(str: string, alpha?: number) {
    return new Color(str, alpha)
  }

  public static rgb(r: number, g: number, b: number): Color {
    return Color.rgba(r, g, b, 1.0)
  }

  public static rgba(r: number, g: number, b: number, a: number): Color {
    return new Color({ r, g, b, a })
  }

  public static hsl(h: number, s: number, l: number): Color {
    return Color.hsla(h, s, l, 1.0)
  }

  public static hsla(h: number, s: number, l: number, a: number): Color {
    h = (h % 360) / 360
    s = clamp01(s)
    l = clamp01(l)
    a = clamp01(a)

    const m2 = l <= 0.5 ? l * (s + 1) : l + s - l * s
    const m1 = l * 2 - m2
    const hue = (val: number): number => {
      val = val < 0 ? val + 1 : val > 1 ? val - 1 : val
      if (val * 6 < 1) {
        return m1 + (m2 - m1) * val * 6
      } else if (val * 2 < 1) {
        return m2
      } else if (val * 3 < 2) {
        return m1 + (m2 - m1) * (2 / 3 - val) * 6
      } else {
        return m1
      }
    }

    return Color.rgba(hue(h + 1 / 3) * 255, hue(h) * 255, hue(h - 1 / 3) * 255, a)
  }

  public static hsv(h: number, s: number, v: number): Color {
    return Color.hsva(h, s, v, 1.0)
  }

  public static hsva(h: number, s: number, v: number, a: number): Color {
    h = ((h % 360) / 360) * 360
    const i = Math.floor((h / 60) % 6)
    const f = h / 60 - i
    const vs = [v, v * (1 - s), v * (1 - f * s), v * (1 - (1 - f) * s)]
    const perm = [
      [0, 3, 1],
      [2, 0, 1],
      [1, 0, 3],
      [1, 2, 0],
      [3, 1, 0],
      [0, 1, 2],
    ]

    return Color.rgba(vs[perm[i][0]] * 255, vs[perm[i][1]] * 255, vs[perm[i][2]] * 255, a)
  }

  private static parseString(str: string): RGBA | null {
    str = str
      .replace(/^[\s,#]+/, '')
      .replace(/\s+$/, '')
      .toLowerCase()

    let match = MATCHERS.rgb.exec(str)
    if (match) {
      return { r: +match[1], g: +match[2], b: +match[3], a: 1 }
    }

    match = MATCHERS.rgba.exec(str)
    if (match) {
      return { r: +match[1], g: +match[2], b: +match[3], a: +match[4] }
    }

    match = MATCHERS.hex6.exec(str)
    if (match) {
      const rgb = match.slice(1, 4).map(c => parseInt(c, 16))
      return { r: rgb[0], g: rgb[1], b: rgb[2], a: 1 }
    }

    match = MATCHERS.hex3.exec(str)
    if (match) {
      const rgb = match.slice(1, 4).map(c => parseInt(c + c, 16))
      return { r: rgb[0], g: rgb[1], b: rgb[2], a: 1 }
    }

    return null
  }

  public isValid(): boolean {
    return this._isValid
  }

  public toRgbaString(): string {
    const { r, g, b, a } = this.toRgba()
    return `rgba(${[r, g, b, a].join(',')})`
  }

  public toRgb(): RGB {
    return {
      r: Math.round(this._r),
      g: Math.round(this._g),
      b: Math.round(this._b),
    }
  }

  public toRgba(): RGBA {
    return {
      ...this.toRgb(),
      a: Math.round(100 * this._a) / 100,
    }
  }

  public toHsl(): HSL {
    const r = this._r / 255
    const g = this._g / 255
    const b = this._b / 255
    const a = this._a

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const l = (max + min) / 2
    const d = max - min
    let h
    let s

    if (max === min) {
      h = s = 0
    } else {
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0)
          break
        case g:
          h = (b - r) / d + 2
          break
        case b:
          h = (r - g) / d + 4
          break
      }
      h /= 6
    }

    return { h: h * 360, s, l, a }
  }

  public luma(): number {
    let r = this._r / 255
    let g = this._g / 255
    let b = this._b / 255

    r = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)
    g = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)
    b = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4)

    return 0.2126 * r + 0.7152 * g + 0.0722 * b
  }

  public lighten(amount: number): Color {
    const { h, s, l, a } = this.toHsl()
    return Color.hsla(h, s, l + amount / 100, a)
  }

  public darken(amount: number): Color {
    const { h, s, l, a } = this.toHsl()
    return Color.hsla(h, s, l - amount / 100, a)
  }

  public fade(amount: number) {
    const { h, s, l } = this.toHsl()
    return Color.hsla(h, s, l, amount / 100)
  }

  public fadeout(amount: number, relative = false) {
    const { h, s, l, a } = this.toHsl()
    if (relative) {
      amount = amount * a
    }
    return Color.hsla(h, s, l, a - amount / 100)
  }

  public static contrast(
    color: Color,
    dark = Color.rgba(255, 255, 255, 1.0),
    light = Color.rgba(0, 0, 0, 1.0),
    threshold = 0.43,
  ): Color {
    // Figure out which is actually light and dark:
    if (dark.luma() > light.luma()) {
      const t = light
      light = dark
      dark = t
    }

    return color.luma() < threshold ? light : dark
  }
}
